import axiosApi from "api/root.api";

export const MAX_IMAGE_BYTES = 100000000;
export const MAX_IMAGE_COUNT = 150;
const states = new Set([
  "uploading",
  "sealing",
  "queued",
  "processing",
  "applied",
  "failed",
  "conflict",
]);

function uuid() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map((n) => n.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function createImageManifest(files) {
  if (!files.length || files.length > MAX_IMAGE_COUNT) throw new Error("IMAGE_COUNT");
  files.forEach((file) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type))
      throw new Error("IMAGE_TYPE");
    if (!file.size || file.size > MAX_IMAGE_BYTES)
      throw Object.assign(new Error("IMAGE_SIZE"), { fileName: file.name });
  });
  const sorted = [...files].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true }),
  );
  return { batchId: uuid(), items: sorted.map((file) => ({ id: uuid(), file })) };
}

export function batchErrorMessage(error, t) {
  const code = error?.response?.data?.errorCode || error?.response?.data?.code || error?.message;
  return t(`scankitNew.batch.errors.${code}`, {
    defaultValue: t("scankitNew.batch.errors.UNKNOWN", {
      code: code || error?.response?.status || "",
    }),
    fileName: error?.fileName,
    status: error?.status,
  });
}

export function createImageBatchClient({ projectId, modelId, token, signal }) {
  const base = `/scan/editor/project/v1/${encodeURIComponent(projectId)}/${encodeURIComponent(modelId)}/image-batches`;
  const config = {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    signal,
    timeout: 30000,
  };
  const read = (response) => {
    const batch = response.data;
    if (
      !batch ||
      !states.has(batch.state) ||
      typeof batch.batchId !== "string" ||
      !Number.isInteger(batch.totalCount) ||
      !Number.isInteger(batch.processedCount)
    ) {
      throw new Error("INVALID_RESPONSE");
    }
    return batch;
  };
  return {
    create: async ({ batchId, items }) => {
      for (let attempt = 0; ; attempt += 1) {
        try {
          return read(
            await axiosApi.post(
              base,
              {
                batchId,
                files: items.map(({ id, file }) => ({
                  id,
                  name: file.name,
                  bytes: file.size,
                  contentType: file.type,
                })),
              },
              config,
            ),
          );
        } catch (error) {
          if (
            signal.aborted ||
            attempt >= 2 ||
            error.response ||
            (!["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK"].includes(error.code) &&
              error.message !== "Network Error")
          )
            throw error;
        }
      }
    },
    uploadUrls: async (batchId, ids) =>
      (await axiosApi.post(`${base}/${encodeURIComponent(batchId)}/upload-urls`, { ids }, config))
        .data,
    complete: async (batchId) =>
      read(
        await axiosApi.post(
          `${base}/${encodeURIComponent(batchId)}/complete-upload`,
          undefined,
          config,
        ),
      ),
    get: async (batchId) =>
      read(await axiosApi.get(`${base}/${encodeURIComponent(batchId)}`, config)),
  };
}

async function putOriginal(target, file, signal) {
  if (signal.aborted) throw new DOMException("Aborted", "AbortError");
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal.addEventListener("abort", cancel, { once: true });
  const timer = setTimeout(cancel, 120000);
  try {
    const response = await fetch(target.url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": target.headers["Content-Type"] },
      credentials: "omit",
      redirect: "error",
      signal: controller.signal,
    });
    if (!response.ok) {
      const error = Object.assign(new Error("UPLOAD_FAILED"), { fileName: file.name });
      error.status = response.status;
      throw error;
    }
  } catch (error) {
    if (controller.signal.aborted && !signal.aborted)
      throw Object.assign(new Error("UPLOAD_TIMEOUT"), { fileName: file.name });
    throw error;
  } finally {
    clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
  }
}

export async function uploadImageBatch({ client, manifest, signal, onProgress, onCreated }) {
  const batch = await client.create(manifest);
  onCreated(batch.batchId);
  if (batch.state !== "uploading") return batch;
  let uploadedCount = 0;
  const targetsFor = async (items) => {
    const uploads = await client.uploadUrls(
      batch.batchId,
      items.map(({ id }) => id),
    );
    const targets = new Map((uploads || []).map((upload) => [upload.id, upload]));
    items.forEach(({ id, file }) => {
      const target = targets.get(id);
      if (
        !target ||
        !/^https:\/\//.test(target.url) ||
        target.headers?.["Content-Type"] !== file.type
      )
        throw new Error("INVALID_TARGET");
    });
    return targets;
  };
  const uploadItem = async (item, targets) => {
    try {
      await putOriginal(targets.get(item.id), item.file, signal);
    } catch (error) {
      if (error.status !== 403 || signal.aborted) throw error;
      const renewed = await targetsFor([item]);
      await putOriginal(renewed.get(item.id), item.file, signal);
    }
    uploadedCount += 1;
    onProgress({ ...batch, phase: "uploading", uploadedCount });
  };
  for (let next = 0; next < manifest.items.length; next += 3) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    const items = manifest.items.slice(next, next + 3);
    const targets = await targetsFor(items);
    const results = await Promise.allSettled(items.map((item) => uploadItem(item, targets)));
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    const failure = results.find((result) => result.status === "rejected");
    if (failure)
      throw failure.reason instanceof Error ? failure.reason : new Error("UPLOAD_FAILED");
  }
  return client.complete(manifest.batchId);
}

export function abortableDelay(ms, signal) {
  return new Promise((resolve, reject) => {
    let timer;
    const abort = () => {
      clearTimeout(timer);
      reject(new DOMException("Aborted", "AbortError"));
    };
    timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, ms);
    if (signal.aborted) abort();
    else signal.addEventListener("abort", abort, { once: true });
  });
}

export async function pollImageBatch({
  client,
  batchId,
  signal,
  onProgress,
  maxAttempts = 60,
  delay = abortableDelay,
}) {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (signal.aborted) throw new DOMException("Aborted", "AbortError");
    const batch = await client.get(batchId);
    onProgress({ ...batch, phase: "processing" });
    if (batch.state === "applied") return batch;
    if (["failed", "conflict"].includes(batch.state))
      throw new Error(batch.errorCode || batch.state);
    if (attempt + 1 < maxAttempts)
      await delay(Math.min(1000 * 2 ** Math.min(attempt, 4), 15000), signal);
  }
  return null;
}
